import { Logger, sharedRef } from '@vue-storefront/core';
import { computed } from '@nuxtjs/composition-api';
import type { Ref } from '@nuxtjs/composition-api';
import { useUser } from '@vsf-enterprise/commercetools';
import type { Address, CustomQuery, UseUserBillingErrors } from '~/types/commercetools';
import { useCustomerAddressApi } from '~/utils/ct/composables/api/useCustomerAddressApi';

export function useUserBilling() {
  const { user, setUser } = useUser();
  const userId = user.value?.id;

  const {
    addCustomerAddress,
    deleteCustomerAddress,
    updateCustomerAddress,
    setDefaultCustomerAddress,
  } = useCustomerAddressApi();

  const loading: Ref<boolean> = sharedRef(false, `useUserBilling-loading-${userId}`);

  const error: Ref<UseUserBillingErrors> = sharedRef({
    addAddress: null,
    deleteAddress: null,
    updateAddress: null,
    load: null,
    setDefaultAddress: null,
  }, `useUserBilling-error-${userId}`);

  async function addAddress(
    params: { address: Partial<Address>, customQuery?: CustomQuery }) {
    Logger.debug('useUserBilling/addAddress', params);

    try {
      loading.value = true;
      const updatedUser = await addCustomerAddress(user.value, params.address);
      setUser(updatedUser);
    } catch (err) {
      error.value.addAddress = err;
      Logger.error('useUserBilling/addAddress', params.address);
    } finally {
      loading.value = false;
    }
  }

  async function deleteAddress(
    params: { address: Partial<Address>, customQuery?: CustomQuery }) {
    try {
      loading.value = true;
      const updatedUser = await deleteCustomerAddress(user.value, params.address);
      setUser(updatedUser);
    } catch (err) {
      error.value.deleteAddress = err;
      Logger.error(`useUserBilling/${userId}/deleteAddress`, err);
    } finally {
      loading.value = false;
    }
  }

  async function updateAddress(
    params: { address: Partial<Address>, customQuery?: CustomQuery }) {
    try {
      loading.value = true;
      const updatedUser = await updateCustomerAddress(user.value, params.address);
      setUser(updatedUser);
    } catch (err) {
      error.value.updateAddress = err;
      Logger.error(`useUserBilling/${userId}/updateAddress`, err);
    } finally {
      loading.value = false;
    }
  }

  async function setDefaultAddress(
    params: { address: Partial<Address>, customQuery?: CustomQuery }) {
    try {
      loading.value = true;
      setUser(await setDefaultCustomerAddress(user.value, params.address));
    } catch (err) {
      error.value.setDefaultAddress = err;
      Logger.error(`useUserBilling/${userId}/setDefaultAddress`, err);
    } finally {
      loading.value = false;
    }
  }

  return {
    customer: computed(() => user.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    addAddress,
    deleteAddress,
    updateAddress,
    setDefaultAddress,
  };
}

export default useUserBilling;
